import { useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { ApiError, UNEXPECTED_MESSAGE } from '@/api/errors';
import { PageHeading } from '@/components/layout/PageHeading';
import { Button } from '@/components/ui/button';

/**
 * A route that could not load, most often because the session check could not
 * reach the API. Says so and offers a retry, rather than a blank screen or a
 * sign-in page the user does not need (a signed-in user stays signed in).
 */
export function RouteErrorPage({ error }: ErrorComponentProps) {
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);
  const message = error instanceof ApiError ? error.message : UNEXPECTED_MESSAGE;
  const retry = () => {
    setRetrying(true);
    void router.invalidate().finally(() => { setRetrying(false); });
  };
  return (
    <div role="alert" className="flex min-h-dvh flex-1 items-center justify-center px-gutter py-16">
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <AlertTriangle className="size-6 text-neg-ink" aria-hidden="true" />
        <PageHeading visible>Lemon AI couldn’t load this page</PageHeading>
        <p className="m-0 text-muted">{message}</p>
        <Button variant="primary" onClick={retry} loading={retrying}>
          Try again
        </Button>
      </div>
    </div>
  );
}
